import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi, categoriesApi } from '@/services/api';
import { ArrowLeft, Image, Package, Type, Check, X, Plus, Loader2, Currency } from 'lucide-react';
import { UPLOADS_URL } from '@/utils/utils';

// Esquema de validación (reutilizado del formulario de creación)
const productSchema = z.object({
    name: z.string()
        .min(3, 'El nombre debe tener al menos 3 caracteres')
        .max(100, 'El nombre no puede exceder 100 caracteres'),
    description: z.string()
        .min(10, 'La descripción debe tener al menos 10 caracteres')
        .max(2000, 'La descripción no puede exceder 2000 caracteres'),
    short_description: z.string().optional(),
    price: z.number()
        .min(0.01, 'El precio debe ser mayor a 0')
        .max(100000, 'El precio no puede exceder 100,000'),
    stock_quantity: z.number()
        .min(0, 'El stock no puede ser negativo')
        .max(10000, 'El stock no puede exceder 10,000'),
    sku: z.string()
        .min(3, 'El SKU debe tener al menos 3 caracteres')
        .max(50, 'El SKU no puede exceder 50 caracteres'),
    category_id: z.number().min(1, 'Selecciona una categoría'),
    status: z.enum(['active', 'inactive', 'draft']),
});

type ProductFormData = z.infer<typeof productSchema>;

const SellerProductEditPage: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [images, setImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [existingImages, setExistingImages] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);

    // Obtener el producto existente
    const { data: product, isLoading, isError } = useQuery({
        queryKey: ['seller-product', productId],
        queryFn: () => productsApi.getProductById(Number(productId)),
        select: (response) => response ? response : null,
    });

    // Obtener categorías
    useEffect(() => {
        categoriesApi.getCategoriesForSeller({}).then(data => setCategories(data));
    }, []);

    // Inicializar el formulario con los datos del producto
    const { register, handleSubmit, formState: { errors }, reset } = useForm<ProductFormData>({
        resolver: zodResolver(productSchema),
    });

    useEffect(() => {
        if (product) {
            reset({
                name: product.name,
                description: product.description,
                short_description: product.short_description,
                price: product.price,
                stock_quantity: product.stock_quantity,
                sku: product.sku,
                category_id: product.category_id,
                status: product.status,
            });
            setExistingImages(product.images || []);
        }
    }, [product, reset]);

    // Mutación para actualizar el producto
    const updateMutation = useMutation({
        mutationFn: (data: Partial<ProductFormData>) =>
            productsApi.updateProduct(Number(productId), data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['seller-products', productId] });
            toast.success('Producto actualizado correctamente');
        },
        onError: () => {
            toast.error('Error al actualizar el producto');
        },
    });

    // Mutación para subir imágenes
    const uploadImagesMutation = useMutation({
        mutationFn: (files: File[]) =>
            productsApi.uploadProductImages(Number(productId), files),
        onSuccess: () => {
            //queryClient.invalidateQueries(['seller-product', productId]);
            queryClient.invalidateQueries({ queryKey: ['seller-products', productId] });
            toast.success('Imágenes subidas correctamente');
        },
        onError: () => {
            toast.error('Error al subir las imágenes');
        },
    });

    // Mutación para eliminar imagen
    const deleteImageMutation = useMutation({
        mutationFn: (imageId: number) =>
            productsApi.deleteProductImage(Number(productId), imageId),
        onSuccess: () => {
            //queryClient.invalidateQueries(['seller-product', productId]);
            queryClient.invalidateQueries({ queryKey: ['seller-products', productId] });
            toast.success('Imagen eliminada correctamente');
        },
        onError: () => {
            toast.error('Error al eliminar la imagen');
        },
    });

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            const validImages = files.filter(file =>
                file.type.match('image.*') && file.size <= 5 * 1024 * 1024
            );

            if (validImages.length !== files.length) {
                toast.error('Algunas imágenes no son válidas (solo JPG/PNG, max 5MB)');
            }

            setImages(prev => [...prev, ...validImages]);

            // Crear previews
            validImages.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        setImagePreviews(prev => [...prev, e.target?.result as string]);
                    }
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const removeImage = (index: number) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingImage = (imageId: number) => {
        if (confirm('¿Estás seguro de eliminar esta imagen?')) {
            deleteImageMutation.mutate(imageId);
        }
    };

    const onSubmit = async (data: ProductFormData) => {
        setIsSubmitting(true);
        try {
            // Actualizar datos del producto
            await updateMutation.mutateAsync(data);
            queryClient.invalidateQueries({
                queryKey: ['seller-product', productId],
                exact: true,
            });

            // Subir nuevas imágenes si hay
            if (images.length > 0) {
                await uploadImagesMutation.mutateAsync(images);
            }

        } finally {
            setIsSubmitting(false);
            navigate(`/seller/products/${productId}`);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    if (isError || !product) {
        return (
            <div className="p-4 text-center text-red-600">
                Error al cargar el producto. Intenta nuevamente.
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <button
                        onClick={() => navigate(`/seller/products/${productId}`)}
                        className="flex items-center text-primary-600 hover:text-primary-800 mb-4"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Volver al producto
                    </button>

                    <h1 className="text-2xl font-bold text-gray-900">
                        <Package className="inline-block w-6 h-6 mr-2 text-primary-600" />
                        Editar Producto
                    </h1>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Sección de imágenes */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Image className="w-5 h-5 mr-2 text-blue-600" />
                            Imágenes del Producto
                        </h2>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
                            {/* Imágenes existentes */}
                            {existingImages.map((image, index) => (
                                <div key={index} className="relative group">
                                    <img
                                        src={UPLOADS_URL + image.image_url}
                                        alt={`Imagen ${index + 1}`}
                                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeExistingImage(image.id)}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                    {image.is_primary && (
                                        <span className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                            Principal
                                        </span>
                                    )}
                                </div>
                            ))}

                            {/* Nuevas imágenes */}
                            {imagePreviews.map((preview, index) => (
                                <div key={`new-${index}`} className="relative group">
                                    <img
                                        src={preview}
                                        alt={`Nueva imagen ${index}`}
                                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}

                            <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                                <input
                                    type="file"
                                    multiple
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    accept="image/*"
                                />
                                <Plus className="w-8 h-8 text-gray-400 mb-2" />
                                <span className="text-sm text-gray-600">Agregar imágenes</span>
                            </label>
                        </div>
                        <p className="text-xs text-gray-500">
                            Máximo 10 imágenes. Formatos: JPG, PNG. Tamaño máximo: 5MB cada una.
                        </p>
                    </div>

                    {/* Información básica */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Type className="w-5 h-5 mr-2 text-primary-600" />
                            Información Básica
                        </h2>

                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre del Producto *
                                </label>
                                <input
                                    {...register('name')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Ej: Smartphone XYZ 2023"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción *
                                </label>
                                <textarea
                                    {...register('description')}
                                    rows={4}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Describe detalladamente tu producto..."
                                />
                                {errors.description && (
                                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción Corta
                                </label>
                                <textarea
                                    {...register('short_description')}
                                    rows={2}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-gray-300"
                                    placeholder="Describe brevemente tu producto..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Estado del Producto
                                </label>
                                <select
                                    {...register('status')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="active">Activo</option>
                                    <option value="inactive">Inactivo</option>
                                    <option value="draft">Borrador</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Precio e inventario */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Currency className="w-5 h-5 mr-2 text-green-600" />
                            Precio e Inventario
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Precio (PEN) *
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">S/ </span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('price', { valueAsNumber: true })}
                                        className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.price ? 'border-red-300' : 'border-gray-300'}`}
                                        placeholder="0.00"
                                    />
                                </div>
                                {errors.price && (
                                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Cantidad en Stock *
                                </label>
                                <input
                                    disabled
                                    type="number"
                                    //{...register('stock_quantity', { valueAsNumber: true })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.stock_quantity ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="0"
                                />
                                {errors.stock_quantity && (
                                    <p className="mt-1 text-sm text-red-600">{errors.stock_quantity.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    SKU (Código de producto) *
                                </label>
                                <input
                                    {...register('sku')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.sku ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Ej: PROD-001"
                                />
                                {errors.sku && (
                                    <p className="mt-1 text-sm text-red-600">{errors.sku.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Categoría *
                                </label>
                                <select
                                    {...register('category_id', { valueAsNumber: true })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.category_id ? 'border-red-300' : 'border-gray-300'}`}
                                >
                                    <option value="">Selecciona una categoría</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.category_id && (
                                    <p className="mt-1 text-sm text-red-600">{errors.category_id.message}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={() => navigate(`/seller/products/${productId}`)}
                            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Guardar Cambios
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SellerProductEditPage;